﻿<script setup lang="ts">
    import { reactive, onMounted, inject, computed } from 'vue';
    import Loading from '@shared/components/Loading.vue';
    import selectCreditCards from '@cwd/Account/CreditCards/SelectCreditCard.vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { CreditCardReference } from '@account/CreditCards/CreditCards.access';
    import { settleAndRenew, getPaymentFailedInfo, PaymentFailedInfo } from '@account/Account.access';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { formatMoney } from '@cwd/Shared/utils/formatMoney';
    import { formatDate } from '@cwd/Shared/utils/formatDateTime';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const state = reactive<{
        selectedCreditCard: CreditCardReference | null;
        creditCardSubscription: boolean;
        paymentFailedInfo: PaymentFailedInfo | null;
        termsAccepted: boolean;
        isLoading: boolean;
        isLoadingPaymentFailedInfo: boolean;
        paymentMade: boolean;
        dateTimeFormatOptions: Intl.DateTimeFormatOptions;
    }>({
        selectedCreditCard: null,
        creditCardSubscription: false,
        paymentFailedInfo: null,
        termsAccepted: false,
        isLoading: true,
        isLoadingPaymentFailedInfo: false,
        paymentMade: false,
        dateTimeFormatOptions: { year: 'numeric', month: 'long', day: 'numeric' },
    });

    onMounted(async () => {
        state.isLoading = false;
        state.creditCardSubscription = contextAccess.state.currentSubscription?.DefaultPaymentMethod == 'CreditCard';

        if (state.creditCardSubscription) {
            state.isLoadingPaymentFailedInfo = true;
            await getPaymentFailedInfo().then((paymentFailedInfo) => {
                state.isLoadingPaymentFailedInfo = false;
                state.paymentFailedInfo = paymentFailedInfo;
            });
        }
    });

    function creditCardSelected(selectedCreditCard: CreditCardReference): void {
        state.selectedCreditCard = selectedCreditCard;
    }

    async function pay(): Promise<void> {
        if (!payEnabled) return;

        state.isLoading = true;

        await settleAndRenew().then(() => {
            state.paymentMade = true;

            bsAlert({ title: $Resources.AlertSuccessHeading, content: $Resources.PaymentNeededAlertBody });

            setTimeout(() => {
                document.location = '/Dashboard';
            }, 2000);
        });
    }

    const payEnabled = computed(() => {
        return (
            !state.isLoading &&
            state.termsAccepted &&
            state.selectedCreditCard != null &&
            new Date(state.selectedCreditCard.Expires) > new Date() &&
            contextAccess.state.currentAccount?.Status === 2
        );
    });

    const paymentNeededSubscriptionPaidUntilString = (): string => {
        if (!state.paymentFailedInfo || state.paymentFailedInfo!.PaidUntil == null) return '';

        return $Resources.PaymentNeededSubscriptionPaidUntil.replace(
            '{{PaidUntil}}',
            formatDate(new Date(state.paymentFailedInfo!.PaidUntil), state.dateTimeFormatOptions)
        );
    };

    const paymentNeededUnpublishWarningString = (): string => {
        if (!state.paymentFailedInfo || state.paymentFailedInfo!.GraceDate == null) return '';

        return $Resources.PaymentNeededUnpublishWarning.replace(
            '{{GraceDate}}',
            formatDate(new Date(state.paymentFailedInfo!.GraceDate), state.dateTimeFormatOptions)
        );
    };

    const oneTimeChargeString = (): string => {
        if (state.paymentFailedInfo == null) return '';

        return $Resources.UpgradeAccountAcceptOneTimeChargeMessage.replace('{{TotalDue}}', state.paymentFailedInfo.TotalDue.toFixed(2)).replace(
            '{{CurrencyCode}}',
            state.paymentFailedInfo.Currency.CurrencyCode.toString()
        );
    };

    const recurringChargeString = (): string => {
        if (state.paymentFailedInfo == null) return '';

        return $Resources.UpgradeAccountAcceptRecurringChargeMessage.replace(
            '{{NextPaymentDate}}',
            formatDate(new Date(state.paymentFailedInfo.NextPaymentDate), state.dateTimeFormatOptions)
        )
            .replace('{{NextPaymentAmount}}', state.paymentFailedInfo.NextPaymentAmount.toFixed(2))
            .replace('{{CurrencyCode}}', state.paymentFailedInfo.Currency.CurrencyCode.toString());
    };
</script>

<template>
    <div class="container-fluid m-lg-t" v-if="!state.isLoading && !state.paymentMade">
        <div class="max-700 m-auto-r m-xl-b m-auto-l text-center">
            <h1>{{ $Resources.PaymentNeededHeading }}</h1>
            <p>{{ $Resources.PaymentNeededLastPaymentFailed }}</p>
            <p v-if="state.paymentFailedInfo?.PaidUntil != null">
                {{ paymentNeededUnpublishWarningString() }}
            </p>
            <p v-if="state.paymentFailedInfo?.PaidUntil != null">{{ paymentNeededSubscriptionPaidUntilString() }}</p>
        </div>

        <div class="max-700 m-auto-r m-xl-b m-auto-l">
            <div v-if="state.creditCardSubscription">
                <Loading :show="state.isLoadingPaymentFailedInfo"></Loading>
                <div v-if="state.paymentFailedInfo != null && !state.isLoadingPaymentFailedInfo">
                    <div class="table-responsive m-0-t m-lg-b">
                        <ul class="list text-lg fw-600 m-0-all text-nowrap b-0-t">
                            <li class="list-row">
                                <div class="list-cell">
                                    <span>
                                        {{ state.paymentFailedInfo.ServiceSubscriptionTier.Name }}
                                        {{ state.paymentFailedInfo.ServiceLevel.Name }}
                                    </span>
                                </div>
                                <div class="list-cell text-right"></div>
                            </li>
                            <li class="list-row">
                                <div class="list-cell text-md p-sm-t p-sm-b normal">
                                    <span>
                                        {{ state.paymentFailedInfo.ServiceSubscriptionTier.Name }}
                                        {{ state.paymentFailedInfo.ServiceLevel.Name }}
                                    </span>
                                </div>
                                <div class="list-cell text-md p-sm-t p-sm-b normal text-right">
                                    <span>
                                        {{ formatMoney(state.paymentFailedInfo.OverDraftAmount, state.paymentFailedInfo.Currency.CurrencyCode, 2) }}
                                    </span>
                                </div>
                            </li>

                            <li class="list-row" v-if="state.paymentFailedInfo.VatAmount > 0">
                                <div class="list-cell text-md p-sm-t p-sm-b normal">
                                    {{ $Resources.VatLabel }}
                                </div>
                                <div class="list-cell text-md p-sm-t p-sm-b normal text-right">
                                    <span>
                                        {{ formatMoney(state.paymentFailedInfo.VatAmount, state.paymentFailedInfo.Currency.CurrencyCode, 2) }}
                                    </span>
                                </div>
                            </li>
                            <li class="list-row list-row-summary p-relative">
                                <div class="list-cell b-0-b">
                                    {{ $Resources.TotalDueLabel }}
                                </div>
                                <div class="list-cell b-0-b text-right">
                                    <span>
                                        {{ formatMoney(state.paymentFailedInfo.TotalDue, state.paymentFailedInfo.Currency.CurrencyCode, 2) }}
                                    </span>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <selectCreditCards
                        @creditCardSelected="creditCardSelected"
                        :selectedCreditCardId="state.selectedCreditCard?.CreditCardId"
                        :currentAccount="contextAccess.state.currentAccount"></selectCreditCards>

                    <ul class="list-checklist list-unstyled">
                        <li>
                            <span v-html="oneTimeChargeString()"></span>
                        </li>
                        <li>
                            <span v-html="recurringChargeString()"></span>
                        </li>
                    </ul>

                    <div class="form-group">
                        <div class="checkbox slide-toggle">
                            <label class="label-lg">
                                <input type="checkbox" v-model="state.termsAccepted" />
                                <span class="toggle"></span>
                                <span v-html="$Resources.UpgradeAccountAcceptTermsLabel"></span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            <p v-else>{{ $Resources.PaymentNeededInvoiceInfo }}</p>
        </div>

        <div class="max-700 m-auto-r m-xl-b m-auto-l" v-if="state.creditCardSubscription">
            <div class="form-group">
                <button class="btn btn-cta btn-lg btn-block" @click="pay" :disabled="!payEnabled">
                    {{ $Resources.PayBtnText }}
                </button>
            </div>
        </div>
    </div>
    <div v-else>
        <Loading :show="true" :fixed="true"></Loading>
    </div>
</template>

<style lang="less"></style>
